/**
 * https://ui.shadcn.com/docs/components/dialog
 */
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";

import { cn } from "@/lib/utils";
import { overlay } from "overlay-kit";
import { useCallback, useMemo } from "react";
import { Button, type ButtonProps } from "./button";
import { ErrorBoundaryWithToast } from "./error-boundary-with-toast";
import { Typography } from "./typography";

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[52] bg-black opacity-40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  hideCloseButton?: boolean;
}

const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  ({ className, children, hideCloseButton = false, ...props }, ref) => {
    return (
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            "fixed left-[50%] top-[50%] z-[52] grid w-[calc(100%-24px)] max-w-2xl desktop:w-full",
            "translate-x-[-50%] translate-y-[-50%] gap-6 bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-[12px]",
            className,
          )}
          {...props}
        >
          {children}
          {!hideCloseButton && (
            <DialogPrimitive.Close className="absolute right-6 top-6 rounded-sm ring-offset-background transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none">
              <i className="inline-block i-bp-close size-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-3 text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-row justify-end space-x-2", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <Typography asChild color="textPrimary" size="lg" font="unbounded">
    <DialogPrimitive.Title ref={ref} className={cn("pr-10", className)} {...props} />
  </Typography>
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};

export function useConfirm() {
  function confirm(props: {
    addon?: React.ReactNode;
    title: string;
    titleClassName?: string;
    description: string;
    confirmText: string;
    confirmVariant?: ButtonProps["variant"];
    cancelText: string;
    cancelVariant?: ButtonProps["variant"];
    className?: string;
  }) {
    return overlay.openAsync<boolean>(({ isOpen, close }) => {
      return (
        <Dialog open={isOpen} onOpenChange={close}>
          <DialogContent className={cn("max-w-[480px]", props.className)}>
            <DialogHeader>
              <div className="flex items-center gap-3">
                {props.addon && <div className="flex justify-center">{props.addon}</div>}
                <DialogTitle className={props.titleClassName}>{props.title}</DialogTitle>
              </div>
              <DialogDescription>{props.description}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                size="md"
                variant={props.cancelVariant ?? "outlined"}
                onClick={() => {
                  close(false);
                }}
              >
                {props.cancelText}
              </Button>
              <Button
                size="md"
                variant={props.confirmVariant ?? "black"}
                onClick={() => {
                  close(true);
                }}
              >
                {props.confirmText}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      );
    });
  }

  return { confirm };
}

export function useModal() {
  let id: string | undefined;
  const open = useCallback(
    async (props: {
      title: string;
      description?: string;
      children: React.ReactNode;
      className?: string;
      titleClassName?: string;
      withNoHeader?: boolean;
      hideCloseButton?: boolean;
      onClose?: () => void;
    }) => {
      id = await overlay.open(({ isOpen, close }) => {
        return (
          <Dialog
            open={isOpen}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                props.onClose?.();
                close();
              }
            }}
          >
            <DialogContent className={props.className} hideCloseButton={props.hideCloseButton}>
              <ErrorBoundaryWithToast>
                {props.withNoHeader ? (
                  <DialogTitle className="sr-only">{props.title}</DialogTitle>
                ) : (
                  <DialogHeader>
                    <DialogTitle className={props.titleClassName}>{props.title}</DialogTitle>
                    {props.description && <DialogDescription>{props.description}</DialogDescription>}
                  </DialogHeader>
                )}
                {props.children}
              </ErrorBoundaryWithToast>
            </DialogContent>
          </Dialog>
        );
      });
    },
    [id],
  );

  return useMemo(() => ({ open, close: () => id && overlay.close(id) }), [open, id]);
}
